/**
 * Bootstrap loader - runs BEFORE React, catches any module load failures
 * and displays actionable error UI even when the main app cannot start.
 * 
 * PERMANENT FIX: Includes watchdog for hanging imports + auto-recovery
 */

const STATUS_ID = 'boot-status';
const ERROR_PANEL_ID = 'boot-error-panel';
const HANG_PANEL_ID = 'boot-hang-panel';

// Watchdog: max time to wait for main module import (adaptive for slower networks)
// Increased from 5s → 10s to survive normal page reloads after SW handoff
const DEFAULT_IMPORT_TIMEOUT_MS = 10000;
const SLOW_IMPORT_TIMEOUT_MS = 12000;
const VERY_SLOW_IMPORT_TIMEOUT_MS = 20000;

type NetworkInfo = {
  effectiveType?: string;
  saveData?: boolean;
};

function getNetworkInfo(): NetworkInfo | undefined {
  const nav = navigator as Navigator & {
    connection?: NetworkInfo;
    mozConnection?: NetworkInfo;
    webkitConnection?: NetworkInfo;
  };
  return nav.connection || nav.mozConnection || nav.webkitConnection;
}

function getMainImportTimeoutMs(): number {
  const info = getNetworkInfo();
  if (!info) return DEFAULT_IMPORT_TIMEOUT_MS;
  if (info.saveData) return VERY_SLOW_IMPORT_TIMEOUT_MS;
  switch (info.effectiveType) {
    case 'slow-2g':
      return VERY_SLOW_IMPORT_TIMEOUT_MS;
    case '2g':
      return VERY_SLOW_IMPORT_TIMEOUT_MS;
    case '3g':
      return SLOW_IMPORT_TIMEOUT_MS;
    default:
      return DEFAULT_IMPORT_TIMEOUT_MS;
  }
}

const MAIN_IMPORT_TIMEOUT_MS = getMainImportTimeoutMs();

// Session keys for recovery state (MUST match what boot-diag.html reads)
const BOOT_RECOVERY_ATTEMPTED_KEY = 'boot_recovery_attempted_session';
const BOOT_HANG_REASON_KEY = 'boot_hang_reason';
const BOOT_ERROR_KEY = 'boot_error';

// Only show boot status in safe mode or when ?debug is present
const _showBootStatus = (() => {
  try {
    const params = new URLSearchParams(location.search);
    return params.has('safe') || params.has('debug');
  } catch {
    return false;
  }
})();

function setStatus(msg: string) {
  if (!_showBootStatus) return; // Hidden in production
  let el = document.getElementById(STATUS_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = STATUS_ID;
    el.style.cssText = `
      position: fixed;
      bottom: 8px;
      left: 8px;
      background: rgba(0,0,0,0.7);
      color: #0f0;
      font-family: monospace;
      font-size: 11px;
      padding: 4px 8px;
      border-radius: 4px;
      z-index: 999999;
      pointer-events: none;
    `;
    document.body.appendChild(el);
  }
  el.textContent = msg;
}

function hideStaticRecovery() {
  const staticRecovery = document.getElementById('static-recovery');
  if (staticRecovery) {
    staticRecovery.style.display = 'none';
  }
}

// Pause the #static-recovery CSS animation while the tab is hidden so the
// 8-second "fade-in" timer only counts time the tab is actually visible.
// This prevents the recovery overlay from popping up immediately when returning
// to a tab that was backgrounded for a long time.
//
// IMPORTANT: Wrapped in DOMContentLoaded so the #static-recovery element
// actually exists when queried. The IIFE was previously running too early
// (before DOM was parsed) causing getElementById to return null silently.
function setupStaticRecoveryVisibilityPause() {
  const el = document.getElementById('static-recovery');
  if (!el) return;

  let hiddenAt: number | null = null;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // Tab going to background — freeze the animation
      hiddenAt = Date.now();
      el.style.animationPlayState = 'paused';
    } else {
      // Tab returning to foreground — resume animation
      hiddenAt = null;
      el.style.animationPlayState = 'running';
    }
  });
}

// Run after DOM is ready so #static-recovery is guaranteed to exist
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupStaticRecoveryVisibilityPause);
} else {
  // DOMContentLoaded already fired (inline script or deferred load)
  setupStaticRecoveryVisibilityPause();
}

function getAppVersion(): string {
  const meta = document.querySelector('meta[name="app-version"]');
  return meta?.getAttribute('content') || 'unknown';
}

function getSWStatus(): string {
  if (!('serviceWorker' in navigator)) return 'unsupported';
  return navigator.serviceWorker.controller ? 'controlled' : 'not-controlled';
}

/**
 * Persist boot error to sessionStorage for diagnostics page
 */
function persistBootError(error: Error | string, filename?: string): void {
  try {
    const errObj = {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack?.slice(0, 2000) : undefined,
      filename: filename || (error instanceof Error ? extractFilename(error.stack) : undefined),
      timestamp: Date.now(),
      appVersion: getAppVersion(),
      swStatus: getSWStatus(),
      url: location.href,
      userAgent: navigator.userAgent.slice(0, 200)
    };
    sessionStorage.setItem(BOOT_ERROR_KEY, JSON.stringify(errObj));
  } catch {
    // Storage unavailable
  }
}

/**
 * Extract vendor chunk filename from error stack for debugging
 */
function extractFilename(stack?: string): string | undefined {
  if (!stack) return undefined;
  const match = stack.match(/\/assets\/(vendor[^:]+\.js)/);
  return match ? match[1] : undefined;
}

function showErrorPanel(error: Error | string, filename?: string) {
  const existing = document.getElementById(ERROR_PANEL_ID);
  if (existing) existing.remove();

  // Persist error for diagnostics
  persistBootError(error, filename);

  const errMsg = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  const stack = error instanceof Error ? error.stack || '' : '';
  const extractedFile = filename || extractFilename(stack);

  const panel = document.createElement('div');
  panel.id = ERROR_PANEL_ID;
  panel.innerHTML = `
    <div style="
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.95);
      color: #fff;
      font-family: system-ui, -apple-system, sans-serif;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 24px;
      z-index: 999999;
      text-align: center;
    ">
      <div style="max-width: 600px; width: 100%;">
        <h1 style="color: #ef4444; font-size: 24px; margin-bottom: 16px;">⚠️ App Failed to Start</h1>
        <p style="color: #9ca3af; margin-bottom: 24px;">
          The application encountered an error during startup.
        </p>
        ${extractedFile ? `
        <div style="background: #451a03; border: 1px solid #f59e0b; border-radius: 6px; padding: 8px 12px; margin-bottom: 16px; font-size: 12px; color: #fcd34d;">
          Failed chunk: <code>${extractedFile}</code>
        </div>
        ` : ''}
        <div style="
          background: #1f2937;
          border: 1px solid #374151;
          border-radius: 8px;
          padding: 16px;
          text-align: left;
          margin-bottom: 24px;
          max-height: 200px;
          overflow: auto;
        ">
          <code style="color: #f87171; font-size: 13px; white-space: pre-wrap; word-break: break-all;">
${errMsg}
${stack ? '\n\nStack:\n' + stack.slice(0, 1000) : ''}
          </code>
        </div>
        <div style="display: flex; gap: 12px; flex-wrap: wrap; justify-content: center;">
          <button id="boot-copy-error" style="
            background: #3b82f6;
            color: #fff;
            border: none;
            padding: 10px 20px;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
          ">📋 Copy Error</button>
          <a href="/pwa-reset.html" style="
            background: #ef4444;
            color: #fff;
            text-decoration: none;
            padding: 10px 20px;
            border-radius: 6px;
            font-size: 14px;
          ">🔄 Reset Cache</a>
          <a href="/?safe=1" style="
            background: #f59e0b;
            color: #000;
            text-decoration: none;
            padding: 10px 20px;
            border-radius: 6px;
            font-size: 14px;
          ">🛡️ Safe Mode</a>
          <a href="/boot-diag.html" style="
            background: #6366f1;
            color: #fff;
            text-decoration: none;
            padding: 10px 20px;
            border-radius: 6px;
            font-size: 14px;
          ">🔍 Diagnostics</a>
        </div>
        <button id="boot-hard-reload" style="
          margin-top: 12px;
          background: #6b7280;
          color: #fff;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
        ">🔃 Hard Reload</button>
      </div>
    </div>
  `;
  document.body.appendChild(panel);

  document.getElementById('boot-copy-error')?.addEventListener('click', () => {
    const report = `QuantumShieldAI Boot Error Report
Time: ${new Date().toISOString()}
URL: ${location.href}
UserAgent: ${navigator.userAgent}
Online: ${navigator.onLine}
AppVersion: ${getAppVersion()}
SWStatus: ${getSWStatus()}
${extractedFile ? `FailedChunk: ${extractedFile}` : ''}

Error:
${errMsg}
${stack ? '\nStack:\n' + stack : ''}
`;
    navigator.clipboard.writeText(report).then(() => {
      const btn = document.getElementById('boot-copy-error');
      if (btn) btn.textContent = '✓ Copied!';
    });
  });

  document.getElementById('boot-hard-reload')?.addEventListener('click', () => {
    location.href = location.origin + '/?_=' + Date.now();
  });
}

function showHangPanel(reason: string) {
  const existing = document.getElementById(HANG_PANEL_ID);
  if (existing) existing.remove();
  
  // Also hide static recovery since we're showing our own
  hideStaticRecovery();

  const panel = document.createElement('div');
  panel.id = HANG_PANEL_ID;
  panel.innerHTML = `
    <div style="
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.95);
      color: #fff;
      font-family: system-ui, -apple-system, sans-serif;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 24px;
      z-index: 999999;
      text-align: center;
    ">
      <div style="max-width: 600px; width: 100%;">
        <h1 style="color: #f59e0b; font-size: 24px; margin-bottom: 16px;">⏳ App Stuck Loading</h1>
        <p style="color: #9ca3af; margin-bottom: 16px;">
          The application is taking too long to load. This may be due to cached files or network issues.
        </p>
        <div style="
          background: #1f2937;
          border: 1px solid #374151;
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 24px;
          text-align: left;
        ">
          <div style="color: #9ca3af; font-size: 12px; margin-bottom: 8px;">Diagnostic Info:</div>
          <code style="color: #60a5fa; font-size: 12px; display: block;">
Reason: ${reason}
App Version: ${getAppVersion()}
SW Status: ${getSWStatus()}
Online: ${navigator.onLine}
Time: ${new Date().toISOString()}
          </code>
        </div>
        <div style="display: flex; gap: 12px; flex-wrap: wrap; justify-content: center;">
          <button id="hang-reset-cache" style="
            background: #ef4444;
            color: #fff;
            border: none;
            padding: 12px 24px;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
          ">🔄 Reset Cache & Reload</button>
          <a href="/?safe=1" style="
            background: #f59e0b;
            color: #000;
            text-decoration: none;
            padding: 12px 24px;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
          ">🛡️ Safe Mode</a>
          <a href="/boot-diag.html" style="
            background: #6366f1;
            color: #fff;
            text-decoration: none;
            padding: 12px 24px;
            border-radius: 6px;
            font-size: 14px;
          ">🔍 Diagnostics</a>
        </div>
        <button id="hang-copy-report" style="
          margin-top: 16px;
          background: transparent;
          color: #60a5fa;
          border: 1px solid #60a5fa;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
        ">📋 Copy Debug Report</button>
      </div>
    </div>
  `;
  document.body.appendChild(panel);

  // Reset cache button - unregister SW, clear caches, reload
  document.getElementById('hang-reset-cache')?.addEventListener('click', async () => {
    const btn = document.getElementById('hang-reset-cache') as HTMLButtonElement;
    if (btn) {
      btn.disabled = true;
      btn.textContent = '⏳ Resetting...';
    }
    
    try {
      // Unregister all service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(r => r.unregister()));
      }
      
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      // Clear storage
      try {
        localStorage.removeItem('app-version');
        sessionStorage.clear();
      } catch (err) {
        console.warn('[Bootstrap] Storage clear skipped:', err);
      }
      
      // Reload with cache bust
      location.href = location.origin + '/?_reset=' + Date.now();
    } catch (e) {
      console.error('[Bootstrap] Reset failed:', e);
      location.reload();
    }
  });

  // Copy report button
  document.getElementById('hang-copy-report')?.addEventListener('click', () => {
    const report = `QuantumShieldAI Boot Hang Report
Time: ${new Date().toISOString()}
URL: ${location.href}
Reason: ${reason}
AppVersion: ${getAppVersion()}
SWStatus: ${getSWStatus()}
Online: ${navigator.onLine}
UserAgent: ${navigator.userAgent}
`;
    navigator.clipboard.writeText(report).then(() => {
      const btn = document.getElementById('hang-copy-report');
      if (btn) btn.textContent = '✓ Copied!';
    });
  });
}

async function attemptAutoRecovery(): Promise<boolean> {
  console.log('[Bootstrap] Attempting auto-recovery...');
  
  try {
    // Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
      console.log('[Bootstrap] Unregistered', registrations.length, 'service workers');
    }
    
    // Clear all caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
      console.log('[Bootstrap] Cleared', cacheNames.length, 'caches');
    }
    
    // Clear localStorage app version
    try {
      localStorage.removeItem('app-version');
    } catch (err) {
      console.warn('[Bootstrap] Storage cleanup skipped:', err);
    }
    
    return true;
  } catch (e) {
    console.error('[Bootstrap] Auto-recovery failed:', e);
    return false;
  }
}

// Attach global error handlers BEFORE attempting to load main
window.addEventListener('error', (e) => {
  console.error('[Bootstrap] Global error:', e.error || e.message);
  // Extract filename from the error event
  const filename = e.filename?.match(/\/assets\/(vendor[^?#]+\.js)/)?.[1];
  if (e.filename?.includes('/src/') || e.filename?.includes('/assets/')) {
    showErrorPanel(e.error || new Error(e.message), filename);
  }
});

window.addEventListener('unhandledrejection', (e) => {
  console.error('[Bootstrap] Unhandled rejection:', e.reason);
  const msg = e.reason?.message || String(e.reason);
  // Try to extract filename from stack
  const filename = e.reason?.stack?.match(/\/assets\/(vendor[^:]+\.js)/)?.[1];
  if (msg.includes('Failed to fetch') || msg.includes('import') || msg.includes('module')) {
    showErrorPanel(e.reason instanceof Error ? e.reason : new Error(msg), filename);
  }
});

// Start bootstrap
setStatus('Boot: bootstrap loaded');

(async () => {
  // Check if we're in safe mode - skip caching/PWA
  const urlParams = new URLSearchParams(location.search);
  const isSafeMode = urlParams.has('safe');
  
  if (isSafeMode) {
    console.log('[Bootstrap] Safe mode enabled - skipping cache checks');
    setStatus('Boot: safe mode active');
    
    // Unregister SW in safe mode
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
    }
  }

  // Create a promise that times out — but pauses when the page is hidden
  // (browsers throttle timers on background tabs, causing false timeouts)
  let importResolved = false;
  
  const timeoutPromise = new Promise<never>((_, reject) => {
    let elapsed = 0;
    let lastTick = Date.now();
    let timerId: ReturnType<typeof setInterval> | null = null;

    const tick = () => {
      if (document.visibilityState === 'hidden') {
        // Tab is backgrounded — don't count this time
        lastTick = Date.now();
        return;
      }
      const now = Date.now();
      elapsed += now - lastTick;
      lastTick = now;

      if (!importResolved && elapsed >= MAIN_IMPORT_TIMEOUT_MS) {
        if (timerId !== null) clearInterval(timerId);
        reject(new Error('IMPORT_TIMEOUT'));
      }
    };

    timerId = setInterval(tick, 250);

    // Also resume the clock when the page becomes visible again
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        lastTick = Date.now(); // reset the tick so hidden time is excluded
      }
    });
  });
  
  const importPromise = (async () => {
    setStatus('Boot: loading main module...');
    await import('./main.tsx');
    importResolved = true;
    setStatus('Boot: main module loaded');
    return true;
  })();

  try {
    await Promise.race([importPromise, timeoutPromise]);
    
    // Success - hide static recovery and status
    hideStaticRecovery();
    setTimeout(() => {
      const el = document.getElementById(STATUS_ID);
      if (el) el.remove();
    }, 2000);
    
    // Clear recovery state on success
    try {
      sessionStorage.removeItem(BOOT_RECOVERY_ATTEMPTED_KEY);
      sessionStorage.removeItem(BOOT_HANG_REASON_KEY);
      sessionStorage.removeItem(BOOT_ERROR_KEY);
    } catch (err) {
      console.warn('[Bootstrap] Recovery state cleanup skipped:', err);
    }
    
  } catch (err) {
    const isTimeout = err instanceof Error && err.message === 'IMPORT_TIMEOUT';
    
    if (isTimeout) {
      console.error('[Bootstrap] Main module import timed out after', MAIN_IMPORT_TIMEOUT_MS, 'ms');
      setStatus('Boot: TIMEOUT');
      
      const reason = 'main_module_import_timeout';
      try {
        sessionStorage.setItem(BOOT_HANG_REASON_KEY, reason);
      } catch (err) {
        console.warn('[Bootstrap] Recovery reason storage skipped:', err);
      }
      
      // Check if we already attempted recovery this session
      const alreadyAttempted = sessionStorage.getItem(BOOT_RECOVERY_ATTEMPTED_KEY) === 'true';
      
      if (!alreadyAttempted) {
        // First attempt - try auto-recovery
        console.log('[Bootstrap] First timeout - attempting auto-recovery...');
        try {
          sessionStorage.setItem(BOOT_RECOVERY_ATTEMPTED_KEY, 'true');
        } catch (err) {
          console.warn('[Bootstrap] Recovery flag storage skipped:', err);
        }
        
        const recovered = await attemptAutoRecovery();
        if (recovered) {
          // Reload with cache-bust param
          console.log('[Bootstrap] Auto-recovery complete, reloading...');
          location.href = location.origin + location.pathname + '?_bootfix=' + Date.now();
          return;
        }
      }
      
      // Either already attempted or recovery failed - show hang panel
      console.log('[Bootstrap] Showing hang panel (recovery already attempted or failed)');
      showHangPanel(reason);
      
    } else {
      // Regular error
      console.error('[Bootstrap] Failed to load main module:', err);
      setStatus('Boot: FAILED');
      showErrorPanel(err instanceof Error ? err : new Error(String(err)));
    }
  }
})();
